import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { Badge } from '@apps/web/components/ui/badge';
import { Input } from '@apps/web/components/ui/input';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import { cn } from '@apps/web/lib/utils';
import {
    formatSeconds,
    RuntimeStatusPill,
} from '@apps/web/routes/_app/admin/voice-config/voice_config_shared';
import { useAdminVoiceConfigOrganizations } from '@apps/web/swr_data_fetching/admin_voice_config_queries';
import { type VoiceConfigOrganizationMatrixRow } from '@libraries/typescript/api_client/schema_types';

const searchSchema = z.object({
    q: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/voice-config/')({
    head: () => ({
        meta: [
            {
                title: 'Voice Config | Phoebe Admin',
            },
        ],
    }),
    validateSearch: searchSchema,
    component: VoiceConfigMatrixPage,
});

// A voice ID is a long opaque vendor string; enough leading characters to tell
// two voices apart without blowing out the column.
const VOICE_ID_PREVIEW_LENGTH = 10;

// Columns whose values are expected to be uniform across the fleet. A cell
// that differs from the column's most common value is the thing an operator
// is scanning for, so it gets a tint.
type OutlierColumn = 'llm' | 'stt' | 'tts' | 'timeout' | 'priority';

function columnKey(
    column: OutlierColumn,
    row: VoiceConfigOrganizationMatrixRow,
): string {
    switch (column) {
        case 'llm':
            return [
                row.llm_primary_model,
                row.llm_primary_reasoning_effort,
                row.llm_fallback_model,
            ].join('|');
        case 'stt':
            return [row.stt_provider, row.turn_detector].join('|');
        case 'tts':
            return [row.tts_service, row.tts_model, row.tts_speed].join('|');
        case 'timeout':
            return String(row.user_turn_stop_timeout_s);
        case 'priority':
            return row.completion_priority;
        default:
            return '';
    }
}

const OUTLIER_COLUMNS: ReadonlyArray<OutlierColumn> = [
    'llm',
    'stt',
    'tts',
    'timeout',
    'priority',
];

/** The most common value per column, used to tint the orgs that differ. */
function computeModalKeys(
    rows: ReadonlyArray<VoiceConfigOrganizationMatrixRow>,
): Record<OutlierColumn, string | null> {
    const modal: Record<OutlierColumn, string | null> = {
        llm: null,
        stt: null,
        tts: null,
        timeout: null,
        priority: null,
    };
    for (const column of OUTLIER_COLUMNS) {
        const counts = new Map<string, number>();
        for (const row of rows) {
            const key = columnKey(column, row);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        let best: string | null = null;
        let bestCount = 0;
        for (const [key, count] of counts) {
            if (count > bestCount) {
                best = key;
                bestCount = count;
            }
        }
        // A column where nothing repeats has no "normal" to deviate from.
        modal[column] = bestCount > 1 ? best : null;
    }
    return modal;
}

const OUTLIER_CELL_CLASS = 'bg-amber-50/70 dark:bg-amber-950/20';

function VoiceConfigMatrixPage() {
    const router = useRouter();
    const navigate = Route.useNavigate();
    const { q } = Route.useSearch();
    const searchQuery = q ?? '';
    const { data, isLoading, error } = useAdminVoiceConfigOrganizations();

    const organizations = useMemo(
        () => data?.organizations ?? [],
        [data?.organizations],
    );
    const modalKeys = useMemo(
        () => computeModalKeys(organizations),
        [organizations],
    );
    const visibleOrganizations = useMemo(() => {
        const needle = searchQuery.trim().toLowerCase();
        if (!needle) {
            return organizations;
        }
        return organizations.filter((organization) =>
            organization.organization_name.toLowerCase().includes(needle),
        );
    }, [organizations, searchQuery]);

    const getOrganizationNavigation = (organizationId: string) => {
        const routeOptions = {
            to: '/admin/voice-config/$organization-id',
            params: { 'organization-id': organizationId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => {
                void router.navigate(routeOptions);
            },
        };
    };

    return (
        <div className="flex h-full flex-col">
            <div className="border-border border-b p-4">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold">Voice Config</h2>
                        <p className="text-muted-foreground text-sm">
                            Every organization’s effective voice stack, side by
                            side. Tinted cells differ from the most common value
                            in that column.
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                        <span className="text-muted-foreground text-sm">
                            {organizations.length} organizations
                        </span>
                        {data ? (
                            <RuntimeStatusPill enabled={data.runtime_enabled} />
                        ) : null}
                    </div>
                </div>
                <div className="relative mt-4 max-w-sm">
                    <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
                    <Input
                        placeholder="Search organizations…"
                        className="pl-9"
                        value={searchQuery}
                        onChange={(event) => {
                            const next = event.target.value;
                            void navigate({
                                search: { q: next === '' ? undefined : next },
                                replace: true,
                            });
                        }}
                    />
                </div>
            </div>
            {isLoading ? (
                <div className="flex h-32 items-center justify-center gap-2">
                    <Spinner className="size-4" />
                    <span className="text-muted-foreground text-sm">
                        Loading voice config…
                    </span>
                </div>
            ) : error ? (
                <div className="text-destructive flex h-32 items-center justify-center text-sm">
                    Could not load the voice config matrix.
                </div>
            ) : visibleOrganizations.length === 0 ? (
                <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
                    {searchQuery
                        ? 'No organizations match this search.'
                        : 'No organizations have voice settings or branches yet.'}
                </div>
            ) : (
                <ScrollArea className="flex-1 overflow-auto">
                    <Table className="min-w-[1180px]">
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[240px]">
                                    Organization
                                </TableHead>
                                <TableHead>LLM</TableHead>
                                <TableHead>Speech</TableHead>
                                <TableHead>TTS</TableHead>
                                <TableHead className="text-right">
                                    Turn timeout
                                </TableHead>
                                <TableHead>Priority</TableHead>
                                <TableHead>Flags</TableHead>
                                <TableHead>Branches</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleOrganizations.map((organization) => (
                                <MatrixRow
                                    key={organization.organization_id}
                                    organization={organization}
                                    modalKeys={modalKeys}
                                    navigation={getOrganizationNavigation(
                                        organization.organization_id,
                                    )}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </ScrollArea>
            )}
        </div>
    );
}

function MatrixRow({
    organization,
    modalKeys,
    navigation,
}: {
    readonly organization: VoiceConfigOrganizationMatrixRow;
    readonly modalKeys: Record<OutlierColumn, string | null>;
    readonly navigation: {
        readonly href: string;
        readonly navigate: () => void;
    };
}) {
    const isOutlier = (column: OutlierColumn) => {
        const modal = modalKeys[column];
        return modal !== null && columnKey(column, organization) !== modal;
    };

    return (
        <TableRow
            className="cursor-pointer"
            {...getNavigableElementProps(navigation)}
        >
            <TableCell className="font-medium">
                {organization.organization_name}
            </TableCell>
            <TableCell className={cn(isOutlier('llm') && OUTLIER_CELL_CLASS)}>
                <div className="flex flex-col gap-0.5">
                    <MonoValue value={organization.llm_primary_model} />
                    <span className="text-muted-foreground text-xs">
                        {organization.llm_primary_reasoning_effort
                            ? `effort ${organization.llm_primary_reasoning_effort}`
                            : 'no reasoning effort'}
                        {organization.llm_fallback_model
                            ? ` · fallback ${organization.llm_fallback_model}`
                            : ' · no fallback'}
                    </span>
                </div>
            </TableCell>
            <TableCell className={cn(isOutlier('stt') && OUTLIER_CELL_CLASS)}>
                <div className="flex flex-col gap-0.5">
                    <MonoValue value={organization.stt_provider} />
                    <span className="text-muted-foreground text-xs">
                        {organization.turn_detector ?? '—'}
                    </span>
                </div>
            </TableCell>
            <TableCell className={cn(isOutlier('tts') && OUTLIER_CELL_CLASS)}>
                <div className="flex flex-col gap-0.5">
                    <span className="text-sm">
                        {organization.tts_service ?? (
                            <span className="text-muted-foreground">—</span>
                        )}
                        {organization.tts_speed !== null ? (
                            <span className="text-muted-foreground">
                                {' '}
                                · {organization.tts_speed}×
                            </span>
                        ) : null}
                    </span>
                    <span className="text-muted-foreground font-mono text-xs">
                        {organization.tts_model ?? '—'}
                    </span>
                    <VoiceIdCell voiceId={organization.tts_voice_id} />
                </div>
            </TableCell>
            <TableCell
                className={cn(
                    'text-right tabular-nums',
                    isOutlier('timeout') && OUTLIER_CELL_CLASS,
                )}
            >
                {organization.user_turn_stop_timeout_s === null ? (
                    <span className="text-muted-foreground">—</span>
                ) : (
                    formatSeconds(organization.user_turn_stop_timeout_s)
                )}
            </TableCell>
            <TableCell
                className={cn(isOutlier('priority') && OUTLIER_CELL_CLASS)}
            >
                {organization.completion_priority}
            </TableCell>
            <TableCell>
                <div className="flex flex-wrap gap-1">
                    {organization.quality_mode_active ? (
                        <Badge
                            variant="outline"
                            className="border-amber-200 bg-amber-100 text-amber-900"
                        >
                            Quality mode
                        </Badge>
                    ) : null}
                    {organization.inbound_voice_agent_enabled ? (
                        <Badge variant="secondary">Inbound</Badge>
                    ) : null}
                </div>
            </TableCell>
            <TableCell className="tabular-nums">
                {organization.active_branch_count === 0 ? (
                    <span className="text-muted-foreground">—</span>
                ) : (
                    `${organization.active_branch_count} · ${organization.total_branch_traffic_percent}%`
                )}
            </TableCell>
        </TableRow>
    );
}

function MonoValue({ value }: { readonly value: string | null }) {
    if (!value) {
        return <span className="text-muted-foreground">—</span>;
    }
    return <span className="font-mono text-xs">{value}</span>;
}

function VoiceIdCell({ voiceId }: { readonly voiceId: string | null }) {
    if (!voiceId) {
        return <span className="text-muted-foreground text-xs">no voice</span>;
    }
    if (voiceId.length <= VOICE_ID_PREVIEW_LENGTH) {
        return (
            <span className="text-muted-foreground font-mono text-xs">
                {voiceId}
            </span>
        );
    }
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <span className="text-muted-foreground w-fit font-mono text-xs">
                    {voiceId.slice(0, VOICE_ID_PREVIEW_LENGTH)}…
                </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="font-mono">
                {voiceId}
            </TooltipContent>
        </Tooltip>
    );
}
